import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { API_BASE_URL, GOOGLE_CLIENT_ID, GA_MEASUREMENT_ID } from '../config/api.js';
import { trackGoogleAnalyticsEvent } from '../utils/googleAnalytics';
import { getVisitorEmail } from '../utils/visitorEmail';

/**
 * Hook za praćenje poseta stranicama
 * Automatski prati posete, klikove i trajanje sesije
 */
export const usePageTracking = (pageType, options = {}) => {
  const location = useLocation();
  const visitIdRef = useRef(null);
  const trackingIdRef = useRef(null);
  const sessionStartTimeRef = useRef(null);
  const clickCountRef = useRef(0);
  const emailRef = useRef(null);

  const { 
    email, 
    skipTracking = false,
    onVisitTracked
  } = options;

  // Generate or get tracking ID
  useEffect(() => {
    if (skipTracking) return;

    const getOrCreateTrackingId = () => {
      let trackingId = sessionStorage.getItem('analytics_tracking_id');
      if (!trackingId) {
        trackingId = `track_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        sessionStorage.setItem('analytics_tracking_id', trackingId);
      }
      trackingIdRef.current = trackingId;
      return trackingId;
    };

    trackingIdRef.current = getOrCreateTrackingId();
  }, [skipTracking]);

  // Function to get email from localStorage
  const getEmailFromStorage = () => {
    try {
      const profile = localStorage.getItem('profile');
      if (profile) {
        const profileData = JSON.parse(profile);
        return profileData?.result?.email || profileData?.email || null;
      }
    } catch (e) {}
    return null;
  };

  // Function to try getting email from Google Identity Services
  // Note: Google One Tap requires user interaction, so this won't work silently
  // We'll use it only if user explicitly interacts with Google Sign-In
  const getEmailFromGoogle = async () => {
    try {
      if (!GOOGLE_CLIENT_ID) return null;
      if (typeof window.google === 'undefined' || !window.google.accounts) {
        await new Promise(resolve => setTimeout(resolve, 2000));
        if (typeof window.google === 'undefined' || !window.google.accounts) return null;
      }

      // Initialize Google Identity Services
      // Note: One Tap requires user interaction, so we can't get email silently
      // This is just for initialization - actual email will come from user interaction
      try {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response) => {
            // This callback fires when user explicitly grants permission via One Tap
            try {
              const payload = JSON.parse(atob(response.credential.split('.')[1]));
              if (payload.email) sessionStorage.setItem('google_email', payload.email);
            } catch (e) {}
          },
          auto_select: false,
          cancel_on_tap_outside: true
        });
      } catch (e) {
        return null;
      }
      const cachedEmail = sessionStorage.getItem('google_email');
      if (cachedEmail) return cachedEmail;
      return null;
    } catch (e) {
      return null;
    }
  };

  // Function to try getting email from browser's credential manager
  // This can get email if user has saved credentials in browser
  const getEmailFromCredentials = async () => {
    try {
      // Check if Credential Management API is available
      if ('credentials' in navigator && 'get' in navigator.credentials) {
        try {
          const credential = await navigator.credentials.get({
            password: true,
            mediation: 'silent' // Try silently without user interaction
          });
          
          if (credential && credential.id && credential.id.includes('@')) return credential.id;
        } catch (e) {
          // User might have denied or API not fully supported
          // This is expected and not an error
        }
      }
    } catch (e) {
      // Credential API might not be available
      // This is expected and not an error
    }
    return null;
  };

  // Track page visit
  useEffect(() => {
    if (skipTracking || !pageType) return;

    const trackVisit = async () => {
      try {
        sessionStartTimeRef.current = new Date();

        // Profil → URL kampanje (?email=) → sesija → Google / browser credentials
        let userEmail = email || getVisitorEmail() || getEmailFromStorage();

        if (!userEmail) {
          const googleEmail = await getEmailFromGoogle();
          if (googleEmail) userEmail = googleEmail;
        }
        if (!userEmail) {
          const credentialEmail = await getEmailFromCredentials();
          if (credentialEmail) userEmail = credentialEmail;
        }
        emailRef.current = userEmail;

        // Location tracking disabled - don't request location permission
        let locationData = null;

        // Get UTM parameters from URL
        const urlParams = new URLSearchParams(window.location.search);
        const utmParams = {
          utmSource: urlParams.get('utm_source'),
          utmMedium: urlParams.get('utm_medium'),
          utmCampaign: urlParams.get('utm_campaign'),
          utmTerm: urlParams.get('utm_term'),
          utmContent: urlParams.get('utm_content')
        };

        // Track in custom analytics (your database)
        const response = await axios.post(`${API_BASE_URL}/api/analytics/track/visit`, {
          email: userEmail || null, // Explicitly send null if no email
          pageType: pageType,
          pagePath: location.pathname + location.search,
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          trackingId: trackingIdRef.current,
          location: locationData,
          ...utmParams
        });

        if (response.data.success) {
          visitIdRef.current = response.data.visitId;
          trackingIdRef.current = response.data.trackingId || trackingIdRef.current;
          
          if (onVisitTracked) {
            onVisitTracked(response.data);
          }
        }

        // Dodatni GA događaj sa tipom stranice (page_view šalje GoogleAnalyticsRouteTracker u App.js)
        if (GA_MEASUREMENT_ID) {
          try {
            trackGoogleAnalyticsEvent('spin_tasker_page', {
              page_type: pageType,
              page_path: location.pathname + location.search,
              ...(userEmail && { user_id: userEmail }),
              ...utmParams
            });
          } catch (gaError) {
          }
        }
      } catch (error) {
        // Fail silently - don't break the app
      }
    };

    trackVisit();

    // Listen for storage changes (when user logs in/out)
    const handleStorageChange = (e) => {
      if (e.key === 'profile') {
        // Update email when profile changes
        const newEmail = getEmailFromStorage();
        if (newEmail && newEmail !== emailRef.current) {
          emailRef.current = newEmail;
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Also check for localStorage changes in the same window (storage event only fires in other tabs)
    const syncVisitEmail = async (nextEmail) => {
      if (!nextEmail || nextEmail === emailRef.current) return;

      emailRef.current = nextEmail;

      try {
        const urlParams = new URLSearchParams(window.location.search);
        await axios.post(`${API_BASE_URL}/api/analytics/track/visit`, {
          email: nextEmail,
          pageType,
          pagePath: location.pathname + location.search,
          userAgent: navigator.userAgent,
          referrer: document.referrer,
          trackingId: trackingIdRef.current,
          utmSource: urlParams.get('utm_source'),
          utmMedium: urlParams.get('utm_medium'),
          utmCampaign: urlParams.get('utm_campaign'),
          utmTerm: urlParams.get('utm_term'),
          utmContent: urlParams.get('utm_content')
        });
      } catch {
        // fail silently
      }
    };

    const checkEmailInterval = setInterval(() => {
      const currentEmail = email || getVisitorEmail() || getEmailFromStorage();
      if (currentEmail) {
        syncVisitEmail(currentEmail);
      }
    }, 2000);

    // Track session end on page unload
    const handleBeforeUnload = () => {
      if (visitIdRef.current || trackingIdRef.current) {
        // Update email one last time before sending
        emailRef.current = emailRef.current || getEmailFromStorage();
        
        // Use sendBeacon for reliable tracking on page unload
        const data = JSON.stringify({
          visitId: visitIdRef.current,
          trackingId: trackingIdRef.current,
          email: emailRef.current,
          pageType: pageType
        });

        navigator.sendBeacon(
          `${API_BASE_URL}/api/analytics/track/session-end`,
          new Blob([data], { type: 'application/json' })
        );
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('beforeunload', handleBeforeUnload);
      clearInterval(checkEmailInterval);
      
      // Track session end when component unmounts
      if (visitIdRef.current || trackingIdRef.current) {
        // Update email one last time
        emailRef.current = emailRef.current || getEmailFromStorage();
        
        const updateSessionEnd = async () => {
          try {
            await axios.post(`${API_BASE_URL}/api/analytics/track/session-end`, {
              visitId: visitIdRef.current,
              trackingId: trackingIdRef.current,
              email: emailRef.current,
              pageType: pageType
            });
          } catch (error) {
          }
        };

        updateSessionEnd();
      }
    };
  }, [pageType, location.pathname, skipTracking, email, onVisitTracked]);

  // Track clicks
  useEffect(() => {
    if (skipTracking || !pageType) return;

    const handleClick = (event) => {
      // Skip tracking for certain elements
      const target = event.target;
      if (target.tagName === 'SCRIPT' || target.tagName === 'STYLE') {
        return;
      }

      clickCountRef.current += 1;

      const trackClick = async () => {
        try {
          await axios.post(`${API_BASE_URL}/api/analytics/track/click`, {
            visitId: visitIdRef.current,
            trackingId: trackingIdRef.current,
            email: emailRef.current,
            element: target.tagName.toLowerCase(),
            elementType: target.type || 'unknown',
            page: location.pathname,
            pageType: pageType
          });
        } catch (error) {
          // Fail silently
        }
      };

      // Debounce click tracking
      clearTimeout(window._clickTrackingTimeout);
      window._clickTrackingTimeout = setTimeout(trackClick, 500);
    };

    document.addEventListener('click', handleClick, true);

    return () => {
      document.removeEventListener('click', handleClick, true);
    };
  }, [pageType, location.pathname, skipTracking]);

  return {
    visitId: visitIdRef.current,
    trackingId: trackingIdRef.current,
    clickCount: clickCountRef.current
  };
};

